import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaBell,
  FaCapsules,
  FaClipboardCheck,
  FaFileMedical,
  FaHeartbeat,
  FaIdCard,
  FaNotesMedical,
  FaRoute,
  FaShieldAlt,
} from "react-icons/fa";
import { supabase } from "../supabaseClient";

const ALERT_TYPES = [
  "Allergy",
  "Falls risk",
  "Infection control",
  "Safeguarding",
  "Communication",
  "Behavioural",
  "Mobility",
  "Other",
];
const ALERT_SEVERITIES = ["Information", "Caution", "High risk"];
const MED_ROUTES = ["Oral", "IV", "IM", "Subcutaneous", "Inhaled", "Topical", "Other"];
const TASK_PRIORITIES = ["Routine", "Urgent", "Immediate"];

function formatDateTime(value) {
  if (!value) return "Not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function roleplayWarningCount(row) {
  let count = 0;
  const hr = Number(row?.heart_rate);
  const rr = Number(row?.respiratory_rate);
  const spo2 = Number(row?.oxygen_saturation);
  const temp = Number(row?.temperature_c);
  const sbp = Number(row?.systolic_bp);

  if (Number.isFinite(hr) && (hr < 50 || hr > 120)) count += 1;
  if (Number.isFinite(rr) && (rr < 10 || rr > 24)) count += 1;
  if (Number.isFinite(spo2) && spo2 < 94) count += 1;
  if (Number.isFinite(temp) && (temp < 35.5 || temp > 38.5)) count += 1;
  if (Number.isFinite(sbp) && (sbp < 90 || sbp > 180)) count += 1;
  if (row?.consciousness && row.consciousness !== "Alert") count += 1;
  return count;
}

function formatRoleplayNhsNumber(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length !== 10) return digits || "Not assigned";
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}

export default function AdvancedClinicalWorkspace({ patient, portalProfile, currentUser }) {
  const patientId = patient?.id;
  const portalAccountId = patient?.portal_account_id || portalProfile?.id || null;
  const [tab, setTab] = useState("observations");
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const [observations, setObservations] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [medications, setMedications] = useState([]);
  const [administrations, setAdministrations] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [handovers, setHandovers] = useState([]);
  const [movements, setMovements] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [consent, setConsent] = useState(null);
  const [incidents, setIncidents] = useState([]);

  const [observationDraft, setObservationDraft] = useState({
    heart_rate: "",
    systolic_bp: "",
    diastolic_bp: "",
    respiratory_rate: "",
    oxygen_saturation: "",
    temperature_c: "",
    consciousness: "Alert",
    supplemental_oxygen: false,
    notes: "",
  });
  const [alertDraft, setAlertDraft] = useState({
    alert_type: "Allergy",
    severity: "Caution",
    title: "",
    details: "",
  });
  const [medDraft, setMedDraft] = useState({
    medication: "",
    dose: "",
    route: "Oral",
    frequency: "",
    instructions: "",
  });
  const [taskDraft, setTaskDraft] = useState({
    title: "",
    details: "",
    priority: "Routine",
    department: patient?.location || "",
    due_at: "",
  });
  const [handoverDraft, setHandoverDraft] = useState({
    department: patient?.location || "",
    current_problem: "",
    plan: "",
    outstanding_tasks: "",
    escalation_concerns: "",
  });
  const [movementDraft, setMovementDraft] = useState({
    from_location: patient?.bed || patient?.location || "",
    to_location: "",
    movement_type: "Transfer",
    notes: "",
  });
  const [documentDraft, setDocumentDraft] = useState({
    document_type: "Clinical letter",
    title: "",
    content: "",
    patient_visible: true,
  });
  const [consentDraft, setConsentDraft] = useState({
    preferred_name: "",
    emergency_contact_name: "",
    emergency_contact_relationship: "",
    emergency_contact_phone: "",
    communication_method: "",
    interpreter_required: false,
    student_observation_consent: false,
    photography_consent: false,
    information_sharing_consent: false,
    notes: "",
  });

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setFeedback(null);

    const [
      observationResult,
      alertResult,
      medicationResult,
      administrationResult,
      taskResult,
      handoverResult,
      movementResult,
      documentResult,
      consentResult,
      incidentResult,
    ] = await Promise.all([
      supabase.from("clinical_observations").select("*").eq("patient_id", patientId).order("recorded_at", { ascending: false }),
      supabase.from("clinical_alerts").select("*").eq("patient_id", patientId).order("created_at", { ascending: false }),
      supabase.from("medication_orders").select("*").eq("patient_id", patientId).order("prescribed_at", { ascending: false }),
      supabase.from("medication_administrations").select("*").eq("patient_id", patientId).order("administered_at", { ascending: false }),
      supabase.from("clinical_tasks").select("*").eq("patient_id", patientId).order("created_at", { ascending: false }),
      supabase.from("handover_entries").select("*").eq("patient_id", patientId).order("created_at", { ascending: false }),
      supabase.from("patient_movements").select("*").eq("patient_id", patientId).order("moved_at", { ascending: false }),
      supabase.from("clinical_documents").select("*").eq("patient_id", patientId).order("created_at", { ascending: false }),
      supabase.from("patient_consents").select("*").eq("patient_id", patientId).maybeSingle(),
      supabase.from("datix_reports").select("id, title, incident_datetime, location, severity, category").eq("patient_id", patientId).order("incident_datetime", { ascending: false }),
    ]);

    const firstError = [
      observationResult,
      alertResult,
      medicationResult,
      administrationResult,
      taskResult,
      handoverResult,
      movementResult,
      documentResult,
      consentResult,
      incidentResult,
    ].find((result) => result.error)?.error;

    if (firstError) {
      console.error("Unable to load advanced clinical workspace:", firstError);
      setFeedback({ type: "error", text: "Some advanced clinical data could not be loaded. Run ADD_HOSPITAL_OPERATIONS_SUITE.sql first." });
    }

    setObservations(observationResult.data || []);
    setAlerts(alertResult.data || []);
    setMedications(medicationResult.data || []);
    setAdministrations(administrationResult.data || []);
    setTasks(taskResult.data || []);
    setHandovers(handoverResult.data || []);
    setMovements(movementResult.data || []);
    setDocuments(documentResult.data || []);
    setIncidents(incidentResult.data || []);

    const nextConsent = consentResult.data || null;
    setConsent(nextConsent);
    setConsentDraft({
      preferred_name: nextConsent?.preferred_name || "",
      emergency_contact_name: nextConsent?.emergency_contact_name || "",
      emergency_contact_relationship: nextConsent?.emergency_contact_relationship || "",
      emergency_contact_phone: nextConsent?.emergency_contact_phone || "",
      communication_method: nextConsent?.communication_method || "",
      interpreter_required: nextConsent?.interpreter_required === true,
      student_observation_consent: nextConsent?.student_observation_consent === true,
      photography_consent: nextConsent?.photography_consent === true,
      information_sharing_consent: nextConsent?.information_sharing_consent === true,
      notes: nextConsent?.notes || "",
    });
    setLoading(false);
  }, [patientId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function insert(table, payload, successText) {
    setWorking(true);
    setFeedback(null);
    const { error } = await supabase.from(table).insert(payload);
    if (error) {
      console.error(error);
      setFeedback({ type: "error", text: error.message || "The record could not be saved." });
    } else {
      setFeedback({ type: "success", text: successText });
      await load();
    }
    setWorking(false);
    return !error;
  }

  async function addObservation(event) {
    event.preventDefault();
    const numeric = (value) => (String(value).trim() === "" ? null : Number(value));
    const saved = await insert(
      "clinical_observations",
      {
        patient_id: patientId,
        portal_account_id: portalAccountId,
        heart_rate: numeric(observationDraft.heart_rate),
        systolic_bp: numeric(observationDraft.systolic_bp),
        diastolic_bp: numeric(observationDraft.diastolic_bp),
        respiratory_rate: numeric(observationDraft.respiratory_rate),
        oxygen_saturation: numeric(observationDraft.oxygen_saturation),
        temperature_c: numeric(observationDraft.temperature_c),
        consciousness: observationDraft.consciousness,
        supplemental_oxygen: observationDraft.supplemental_oxygen,
        notes: observationDraft.notes.trim() || null,
        recorded_by: currentUser?.id || null,
      },
      "Observations recorded.",
    );
    if (saved) setObservationDraft({ heart_rate: "", systolic_bp: "", diastolic_bp: "", respiratory_rate: "", oxygen_saturation: "", temperature_c: "", consciousness: "Alert", supplemental_oxygen: false, notes: "" });
  }

  async function addAlert(event) {
    event.preventDefault();
    if (!alertDraft.title.trim()) return;
    const saved = await insert("clinical_alerts", {
      patient_id: patientId,
      alert_type: alertDraft.alert_type,
      severity: alertDraft.severity,
      title: alertDraft.title.trim(),
      details: alertDraft.details.trim() || null,
      created_by: currentUser?.id || null,
    }, "Clinical alert added.");
    if (saved) setAlertDraft({ alert_type: "Allergy", severity: "Caution", title: "", details: "" });
  }

  async function resolveAlert(row) {
    setWorking(true);
    const { error } = await supabase.from("clinical_alerts").update({ active: false, resolved_by: currentUser?.id || null, resolved_at: new Date().toISOString() }).eq("id", row.id);
    setWorking(false);
    if (error) setFeedback({ type: "error", text: error.message }); else { setFeedback({ type: "success", text: "Alert resolved." }); await load(); }
  }

  async function addMedication(event) {
    event.preventDefault();
    if (!medDraft.medication.trim() || !medDraft.dose.trim()) return;
    const saved = await insert("medication_orders", {
      patient_id: patientId,
      medication: medDraft.medication.trim(),
      dose: medDraft.dose.trim(),
      route: medDraft.route,
      frequency: medDraft.frequency.trim() || null,
      instructions: medDraft.instructions.trim() || null,
      prescribed_by: currentUser?.id || null,
    }, "Medication order added.");
    if (saved) setMedDraft({ medication: "", dose: "", route: "Oral", frequency: "", instructions: "" });
  }

  async function recordAdministration(order, status) {
    setWorking(true);
    const note = window.prompt(`Optional note for ${status}:`, "") || null;
    const { error } = await supabase.from("medication_administrations").insert({
      order_id: order.id,
      patient_id: patientId,
      administration_status: status,
      notes: note,
      administered_by: currentUser?.id || null,
    });
    setWorking(false);
    if (error) setFeedback({ type: "error", text: error.message }); else { setFeedback({ type: "success", text: `${status} administration recorded.` }); await load(); }
  }

  async function stopMedication(order) {
    setWorking(true);
    const { error } = await supabase.from("medication_orders").update({ status: "Stopped", stopped_at: new Date().toISOString() }).eq("id", order.id);
    setWorking(false);
    if (error) setFeedback({ type: "error", text: error.message }); else await load();
  }

  async function addTask(event) {
    event.preventDefault();
    if (!taskDraft.title.trim()) return;
    const saved = await insert("clinical_tasks", {
      patient_id: patientId,
      department: taskDraft.department.trim() || null,
      title: taskDraft.title.trim(),
      details: taskDraft.details.trim() || null,
      priority: taskDraft.priority,
      due_at: taskDraft.due_at ? new Date(taskDraft.due_at).toISOString() : null,
      created_by: currentUser?.id || null,
    }, "Task added to the staff worklist.");
    if (saved) setTaskDraft({ title: "", details: "", priority: "Routine", department: patient?.location || "", due_at: "" });
  }

  async function addHandover(event) {
    event.preventDefault();
    if (!handoverDraft.current_problem.trim()) return;
    const saved = await insert("handover_entries", {
      patient_id: patientId,
      department: handoverDraft.department.trim() || null,
      location: patient?.bed || patient?.location || null,
      current_problem: handoverDraft.current_problem.trim(),
      plan: handoverDraft.plan.trim() || null,
      outstanding_tasks: handoverDraft.outstanding_tasks.trim() || null,
      escalation_concerns: handoverDraft.escalation_concerns.trim() || null,
      created_by: currentUser?.id || null,
    }, "Handover entry added.");
    if (saved) setHandoverDraft({ department: patient?.location || "", current_problem: "", plan: "", outstanding_tasks: "", escalation_concerns: "" });
  }

  async function addMovement(event) {
    event.preventDefault();
    if (!movementDraft.to_location.trim()) return;
    setWorking(true);
    setFeedback(null);
    const { error } = await supabase.from("patient_movements").insert({
      patient_id: patientId,
      from_location: movementDraft.from_location.trim() || null,
      to_location: movementDraft.to_location.trim(),
      movement_type: movementDraft.movement_type,
      notes: movementDraft.notes.trim() || null,
      moved_by: currentUser?.id || null,
    });
    if (!error) {
      await supabase.from("patients").update({ location: movementDraft.to_location.trim() }).eq("id", patientId);
      await supabase.from("patient_records").insert({
        patient_id: patientId,
        added_by: currentUser?.id || null,
        entry: [
          "[Movement]",
          movementDraft.from_location.trim() ? `From: ${movementDraft.from_location.trim()}` : "From: Not recorded",
          `To: ${movementDraft.to_location.trim()}`,
          `Type: ${movementDraft.movement_type}`,
          movementDraft.notes.trim() ? `Notes: ${movementDraft.notes.trim()}` : "",
        ].filter(Boolean).join("\n"),
      });
    }
    setWorking(false);
    if (error) setFeedback({ type: "error", text: error.message });
    else {
      setFeedback({ type: "success", text: `Patient movement recorded to ${movementDraft.to_location.trim()}.` });
      setMovementDraft({ from_location: movementDraft.to_location.trim(), to_location: "", movement_type: "Transfer", notes: "" });
      await load();
    }
  }

  async function addDocument(event) {
    event.preventDefault();
    if (!documentDraft.title.trim() || !documentDraft.content.trim()) return;
    const saved = await insert("clinical_documents", {
      patient_id: patientId,
      portal_account_id: portalAccountId,
      document_type: documentDraft.document_type,
      title: documentDraft.title.trim(),
      content: documentDraft.content.trim(),
      patient_visible: documentDraft.patient_visible,
      created_by: currentUser?.id || null,
    }, "Clinical document saved.");
    if (saved) setDocumentDraft({ document_type: "Clinical letter", title: "", content: "", patient_visible: true });
  }

  async function saveConsent(event) {
    event.preventDefault();
    setWorking(true);
    const { error } = await supabase.from("patient_consents").upsert({
      patient_id: patientId,
      portal_account_id: portalAccountId,
      ...consentDraft,
      updated_by: currentUser?.id || null,
      updated_at: new Date().toISOString(),
    }, { onConflict: "patient_id" });
    setWorking(false);
    if (error) setFeedback({ type: "error", text: error.message }); else { setFeedback({ type: "success", text: "Consent and next-of-kin details saved." }); await load(); }
  }

  function printWristband() {
    const name = consentDraft.preferred_name || patient?.full_name || portalProfile?.roleplay_name || "Patient";
    const nhs = formatRoleplayNhsNumber(patient?.roleplay_nhs_number || portalProfile?.roleplay_nhs_number);
    const dob = patient?.date_of_birth || portalProfile?.date_of_birth || "Not recorded";
    const windowRef = window.open("", "_blank", "width=900,height=500");
    if (!windowRef) return;
    windowRef.document.write(`<!doctype html><html><head><title>RUHT wristband</title><style>body{font-family:Arial,sans-serif;padding:30px}.band{width:760px;border:2px solid #111;padding:18px;display:grid;grid-template-columns:1fr 210px;gap:20px}.nhs{font-size:28px;font-weight:700;color:#005eb8}.name{font-size:24px;font-weight:700;margin:12px 0}.meta{font-size:16px;line-height:1.6}.code{font-family:monospace;letter-spacing:3px;font-size:14px;border:1px solid #111;padding:16px;overflow:hidden}.bars{height:70px;background:repeating-linear-gradient(90deg,#111 0 2px,#fff 2px 5px,#111 5px 6px,#fff 6px 10px);margin-bottom:10px}@media print{button{display:none}}</style></head><body><div class="band"><div><div class="nhs">RUHT</div><div class="name">${name}</div><div class="meta"><strong>Roleplay NHS:</strong> ${nhs}<br><strong>DOB:</strong> ${dob}<br><strong>Location:</strong> ${patient?.bed || patient?.location || "Not assigned"}</div></div><div class="code"><div class="bars"></div>${String(patientId || "").slice(0, 18).toUpperCase()}</div></div><p><button onclick="window.print()">Print wristband</button></p></body></html>`);
    windowRef.document.close();
  }

  const activeAlerts = alerts.filter((row) => row.active !== false);
  const activeMeds = medications.filter((row) => row.status !== "Stopped");
  const openTasks = tasks.filter((row) => row.status !== "Completed");

  const tabs = [
    ["observations", "Observations", FaHeartbeat],
    ["alerts", `Alerts (${activeAlerts.length})`, FaBell],
    ["medications", "Medicines", FaCapsules],
    ["handover", "Tasks & handover", FaClipboardCheck],
    ["movement", "Movement", FaRoute],
    ["documents", "Documents", FaFileMedical],
    ["consent", "Consent / NOK", FaShieldAlt],
    ["incidents", "Incidents", FaNotesMedical],
    ["wristband", "Wristband", FaIdCard],
  ];

  return (
    <section className="pguh-advanced-clinical">
      <div className="pguh-advanced-clinical__header">
        <div>
          <span className="pguh-eyebrow">Clinical record</span>
          <h2 className="nhsuk-heading-m">Patient safety & care tools</h2>
        </div>
        <button type="button" className="nhsuk-button nhsuk-button--secondary nhsuk-u-margin-0" onClick={load} disabled={loading}>Refresh</button>
      </div>

      <div className="pguh-clinical-tabs pguh-advanced-clinical__tabs">
        {tabs.map(([value, label, Icon]) => (
          <button type="button" key={value} className={tab === value ? "is-active" : ""} onClick={() => setTab(value)}><Icon aria-hidden="true" /> {label}</button>
        ))}
      </div>

      {feedback && <div className={`pguh-timesheet-message pguh-timesheet-message--${feedback.type}`}>{feedback.text}</div>}

      {tab === "observations" && (
        <div className="pguh-advanced-grid">
          <form className="pguh-clinical-form" onSubmit={addObservation}>
            <h3 className="nhsuk-heading-s">Record observations</h3>
            <div className="pguh-vitals-grid">
              <label>Heart rate<input className="nhsuk-input" type="number" value={observationDraft.heart_rate} onChange={(e) => setObservationDraft((d) => ({ ...d, heart_rate: e.target.value }))} /></label>
              <label>Blood pressure<input className="nhsuk-input" type="number" placeholder="Systolic" value={observationDraft.systolic_bp} onChange={(e) => setObservationDraft((d) => ({ ...d, systolic_bp: e.target.value }))} /><input className="nhsuk-input" type="number" placeholder="Diastolic" value={observationDraft.diastolic_bp} onChange={(e) => setObservationDraft((d) => ({ ...d, diastolic_bp: e.target.value }))} /></label>
              <label>Respiratory rate<input className="nhsuk-input" type="number" value={observationDraft.respiratory_rate} onChange={(e) => setObservationDraft((d) => ({ ...d, respiratory_rate: e.target.value }))} /></label>
              <label>SpO₂ %<input className="nhsuk-input" type="number" step="0.1" value={observationDraft.oxygen_saturation} onChange={(e) => setObservationDraft((d) => ({ ...d, oxygen_saturation: e.target.value }))} /></label>
              <label>Temperature °C<input className="nhsuk-input" type="number" step="0.1" value={observationDraft.temperature_c} onChange={(e) => setObservationDraft((d) => ({ ...d, temperature_c: e.target.value }))} /></label>
              <label>Consciousness<select className="nhsuk-select" value={observationDraft.consciousness} onChange={(e) => setObservationDraft((d) => ({ ...d, consciousness: e.target.value }))}><option>Alert</option><option>Voice</option><option>Pain</option><option>Unresponsive</option></select></label>
            </div>
            <label className="pguh-inline-check"><input type="checkbox" checked={observationDraft.supplemental_oxygen} onChange={(e) => setObservationDraft((d) => ({ ...d, supplemental_oxygen: e.target.checked }))} /> Supplemental oxygen</label>
            <label className="nhsuk-label">Notes</label><textarea className="nhsuk-textarea" rows="2" value={observationDraft.notes} onChange={(e) => setObservationDraft((d) => ({ ...d, notes: e.target.value }))} />
            <div className="nhsuk-hint">Roleplay observations only. The warning count shown here is not a real NEWS2 or clinical decision-support score.</div>
            <button className="nhsuk-button" disabled={working}>Save observations</button>
          </form>
          <div className="pguh-advanced-list">
            <h3 className="nhsuk-heading-s">Observation history</h3>
            {observations.length === 0 ? <p>No observations recorded.</p> : observations.slice(0, 12).map((row) => <article key={row.id}><div><strong>{formatDateTime(row.recorded_at)}</strong><span className={`pguh-rp-score ${roleplayWarningCount(row) ? "is-warning" : ""}`}>RP warnings {roleplayWarningCount(row)}</span></div><p>HR {row.heart_rate ?? "–"} · BP {row.systolic_bp ?? "–"}/{row.diastolic_bp ?? "–"} · RR {row.respiratory_rate ?? "–"} · SpO₂ {row.oxygen_saturation ?? "–"}% · Temp {row.temperature_c ?? "–"}°C</p><small>{row.consciousness || "Not recorded"}{row.supplemental_oxygen ? " · Oxygen" : ""}</small>{row.notes && <p>{row.notes}</p>}</article>)}
          </div>
        </div>
      )}

      {tab === "alerts" && (
        <div className="pguh-advanced-grid">
          <form className="pguh-clinical-form" onSubmit={addAlert}><h3 className="nhsuk-heading-s">Add clinical alert</h3><label className="nhsuk-label">Type</label><select className="nhsuk-select" value={alertDraft.alert_type} onChange={(e) => setAlertDraft((d) => ({ ...d, alert_type: e.target.value }))}>{ALERT_TYPES.map((v) => <option key={v}>{v}</option>)}</select><label className="nhsuk-label">Severity</label><select className="nhsuk-select" value={alertDraft.severity} onChange={(e) => setAlertDraft((d) => ({ ...d, severity: e.target.value }))}>{ALERT_SEVERITIES.map((v) => <option key={v}>{v}</option>)}</select><label className="nhsuk-label">Alert title</label><input className="nhsuk-input" value={alertDraft.title} onChange={(e) => setAlertDraft((d) => ({ ...d, title: e.target.value }))} required /><label className="nhsuk-label">Details</label><textarea className="nhsuk-textarea" rows="3" value={alertDraft.details} onChange={(e) => setAlertDraft((d) => ({ ...d, details: e.target.value }))} /><button className="nhsuk-button" disabled={working}>Add alert</button></form>
          <div className="pguh-advanced-list"><h3 className="nhsuk-heading-s">Active alerts</h3>{activeAlerts.length === 0 ? <p>No active structured alerts.</p> : activeAlerts.map((row) => <article key={row.id} className={`pguh-alert-card pguh-alert-card--${row.severity.toLowerCase().replace(/\s+/g, "-")}`}><div><strong>{row.title}</strong><span>{row.severity}</span></div><p>{row.alert_type}{row.details ? ` · ${row.details}` : ""}</p><button type="button" className="pguh-text-button" onClick={() => resolveAlert(row)} disabled={working}>Resolve alert</button></article>)}</div>
        </div>
      )}

      {tab === "medications" && (
        <div className="pguh-advanced-grid">
          <form className="pguh-clinical-form" onSubmit={addMedication}><h3 className="nhsuk-heading-s">New medication order</h3><label className="nhsuk-label">Medication</label><input className="nhsuk-input" value={medDraft.medication} onChange={(e) => setMedDraft((d) => ({ ...d, medication: e.target.value }))} required /><label className="nhsuk-label">Dose</label><input className="nhsuk-input" value={medDraft.dose} onChange={(e) => setMedDraft((d) => ({ ...d, dose: e.target.value }))} required /><label className="nhsuk-label">Route</label><select className="nhsuk-select" value={medDraft.route} onChange={(e) => setMedDraft((d) => ({ ...d, route: e.target.value }))}>{MED_ROUTES.map((v) => <option key={v}>{v}</option>)}</select><label className="nhsuk-label">Frequency</label><input className="nhsuk-input" value={medDraft.frequency} onChange={(e) => setMedDraft((d) => ({ ...d, frequency: e.target.value }))} /><label className="nhsuk-label">Instructions</label><textarea className="nhsuk-textarea" rows="2" value={medDraft.instructions} onChange={(e) => setMedDraft((d) => ({ ...d, instructions: e.target.value }))} /><button className="nhsuk-button" disabled={working}>Prescribe</button></form>
          <div className="pguh-advanced-list"><h3 className="nhsuk-heading-s">Active medication / MAR</h3>{activeMeds.length === 0 ? <p>No active medication orders.</p> : activeMeds.map((row) => <article key={row.id}><div><strong>{row.medication} · {row.dose}</strong><span>{row.route || "Route not set"}</span></div><p>{row.frequency || "Frequency not set"}{row.instructions ? ` · ${row.instructions}` : ""}</p><div className="pguh-action-row"><button type="button" className="nhsuk-button nhsuk-button--small" onClick={() => recordAdministration(row, "Administered")}>Administered</button><button type="button" className="nhsuk-button nhsuk-button--secondary nhsuk-button--small" onClick={() => recordAdministration(row, "Refused")}>Refused</button><button type="button" className="nhsuk-button nhsuk-button--secondary nhsuk-button--small" onClick={() => recordAdministration(row, "Missed")}>Missed</button><button type="button" className="pguh-text-button" onClick={() => stopMedication(row)}>Stop</button></div><details><summary>Administration history</summary>{administrations.filter((a) => a.order_id === row.id).slice(0, 8).map((a) => <p key={a.id}><strong>{a.administration_status}</strong> · {formatDateTime(a.administered_at)}{a.notes ? ` · ${a.notes}` : ""}</p>)}</details></article>)}</div>
        </div>
      )}

      {tab === "handover" && (
        <div className="pguh-advanced-grid">
          <div><form className="pguh-clinical-form" onSubmit={addTask}><h3 className="nhsuk-heading-s">Create staff task</h3><label className="nhsuk-label">Task</label><input className="nhsuk-input" value={taskDraft.title} onChange={(e) => setTaskDraft((d) => ({ ...d, title: e.target.value }))} required /><label className="nhsuk-label">Details</label><textarea className="nhsuk-textarea" rows="2" value={taskDraft.details} onChange={(e) => setTaskDraft((d) => ({ ...d, details: e.target.value }))} /><label className="nhsuk-label">Priority</label><select className="nhsuk-select" value={taskDraft.priority} onChange={(e) => setTaskDraft((d) => ({ ...d, priority: e.target.value }))}>{TASK_PRIORITIES.map((v) => <option key={v}>{v}</option>)}</select><label className="nhsuk-label">Due</label><input className="nhsuk-input" type="datetime-local" value={taskDraft.due_at} onChange={(e) => setTaskDraft((d) => ({ ...d, due_at: e.target.value }))} /><button className="nhsuk-button" disabled={working}>Add task</button></form><form className="pguh-clinical-form" onSubmit={addHandover}><h3 className="nhsuk-heading-s">Add handover</h3><label className="nhsuk-label">Current problem</label><textarea className="nhsuk-textarea" rows="2" value={handoverDraft.current_problem} onChange={(e) => setHandoverDraft((d) => ({ ...d, current_problem: e.target.value }))} required /><label className="nhsuk-label">Plan</label><textarea className="nhsuk-textarea" rows="2" value={handoverDraft.plan} onChange={(e) => setHandoverDraft((d) => ({ ...d, plan: e.target.value }))} /><label className="nhsuk-label">Outstanding tasks</label><textarea className="nhsuk-textarea" rows="2" value={handoverDraft.outstanding_tasks} onChange={(e) => setHandoverDraft((d) => ({ ...d, outstanding_tasks: e.target.value }))} /><label className="nhsuk-label">Escalation concerns</label><textarea className="nhsuk-textarea" rows="2" value={handoverDraft.escalation_concerns} onChange={(e) => setHandoverDraft((d) => ({ ...d, escalation_concerns: e.target.value }))} /><button className="nhsuk-button" disabled={working}>Save handover</button></form></div>
          <div className="pguh-advanced-list"><h3 className="nhsuk-heading-s">Open tasks</h3>{openTasks.length === 0 ? <p>No open tasks.</p> : openTasks.map((row) => <article key={row.id}><div><strong>{row.title}</strong><span>{row.priority}</span></div><p>{row.details || "No details"}</p><small>{row.due_at ? `Due ${formatDateTime(row.due_at)}` : "No due time"}</small></article>)}<h3 className="nhsuk-heading-s nhsuk-u-margin-top-5">Recent handover</h3>{handovers.slice(0, 6).map((row) => <article key={row.id}><div><strong>{row.current_problem}</strong><span>{formatDateTime(row.created_at)}</span></div>{row.plan && <p><strong>Plan:</strong> {row.plan}</p>}{row.outstanding_tasks && <p><strong>Outstanding:</strong> {row.outstanding_tasks}</p>}{row.escalation_concerns && <p><strong>Escalation:</strong> {row.escalation_concerns}</p>}</article>)}</div>
        </div>
      )}

      {tab === "movement" && (
        <div className="pguh-advanced-grid">
          <form className="pguh-clinical-form" onSubmit={addMovement}>
            <h3 className="nhsuk-heading-s">Record patient movement</h3>
            <label className="nhsuk-label">From</label>
            <input className="nhsuk-input" value={movementDraft.from_location} onChange={(e) => setMovementDraft((d) => ({ ...d, from_location: e.target.value }))} />
            <label className="nhsuk-label">To</label>
            <input className="nhsuk-input" placeholder="e.g. CT, Majors 3, Ward 5" value={movementDraft.to_location} onChange={(e) => setMovementDraft((d) => ({ ...d, to_location: e.target.value }))} required />
            <label className="nhsuk-label">Movement type</label>
            <select className="nhsuk-select" value={movementDraft.movement_type} onChange={(e) => setMovementDraft((d) => ({ ...d, movement_type: e.target.value }))}>
              <option>Transfer</option><option>Investigation</option><option>Procedure</option><option>Return</option><option>Discharge</option><option>Other</option>
            </select>
            <label className="nhsuk-label">Notes</label>
            <textarea className="nhsuk-textarea" rows="3" value={movementDraft.notes} onChange={(e) => setMovementDraft((d) => ({ ...d, notes: e.target.value }))} />
            <button className="nhsuk-button" disabled={working}>Record movement</button>
          </form>
          <div className="pguh-advanced-list">
            <h3 className="nhsuk-heading-s">Movement history</h3>
            {movements.length === 0 ? <p>No patient movements recorded.</p> : movements.map((row) => <article key={row.id}><div><strong>{row.to_location || "Location released"}</strong><span>{row.movement_type}</span></div><p>{row.from_location ? `From ${row.from_location}` : "Starting location not recorded"}</p>{row.notes && <p>{row.notes}</p>}<small>{formatDateTime(row.moved_at)}</small></article>)}
          </div>
        </div>
      )}

      {tab === "documents" && (
        <div className="pguh-advanced-grid"><form className="pguh-clinical-form" onSubmit={addDocument}><h3 className="nhsuk-heading-s">Create document</h3><label className="nhsuk-label">Type</label><select className="nhsuk-select" value={documentDraft.document_type} onChange={(e) => setDocumentDraft((d) => ({ ...d, document_type: e.target.value }))}><option>Clinical letter</option><option>Appointment letter</option><option>Referral letter</option><option>Investigation report</option><option>Procedure note</option><option>Discharge letter</option><option>Other</option></select><label className="nhsuk-label">Title</label><input className="nhsuk-input" value={documentDraft.title} onChange={(e) => setDocumentDraft((d) => ({ ...d, title: e.target.value }))} required /><label className="nhsuk-label">Content</label><textarea className="nhsuk-textarea" rows="8" value={documentDraft.content} onChange={(e) => setDocumentDraft((d) => ({ ...d, content: e.target.value }))} required /><label className="pguh-inline-check"><input type="checkbox" checked={documentDraft.patient_visible} onChange={(e) => setDocumentDraft((d) => ({ ...d, patient_visible: e.target.checked }))} /> Show in patient portal</label><button className="nhsuk-button" disabled={working}>Save document</button></form><div className="pguh-advanced-list"><h3 className="nhsuk-heading-s">Document centre</h3>{documents.length === 0 ? <p>No documents recorded.</p> : documents.map((row) => <details key={row.id}><summary><strong>{row.title}</strong> · {row.document_type} · {formatDateTime(row.created_at)} {row.patient_visible ? "· Patient visible" : ""}</summary><div className="pguh-document-body">{row.content}</div></details>)}</div></div>
      )}

      {tab === "consent" && (
        <form className="pguh-clinical-form pguh-consent-form" onSubmit={saveConsent}><h3 className="nhsuk-heading-s">Consent, preferences & next of kin</h3><div className="pguh-form-grid"><label>Preferred name<input className="nhsuk-input" value={consentDraft.preferred_name} onChange={(e) => setConsentDraft((d) => ({ ...d, preferred_name: e.target.value }))} /></label><label>Communication method<input className="nhsuk-input" value={consentDraft.communication_method} onChange={(e) => setConsentDraft((d) => ({ ...d, communication_method: e.target.value }))} /></label><label>Emergency contact<input className="nhsuk-input" value={consentDraft.emergency_contact_name} onChange={(e) => setConsentDraft((d) => ({ ...d, emergency_contact_name: e.target.value }))} /></label><label>Relationship<input className="nhsuk-input" value={consentDraft.emergency_contact_relationship} onChange={(e) => setConsentDraft((d) => ({ ...d, emergency_contact_relationship: e.target.value }))} /></label><label>Emergency contact number<input className="nhsuk-input" value={consentDraft.emergency_contact_phone} onChange={(e) => setConsentDraft((d) => ({ ...d, emergency_contact_phone: e.target.value }))} /></label></div><div className="pguh-consent-checks"><label><input type="checkbox" checked={consentDraft.interpreter_required} onChange={(e) => setConsentDraft((d) => ({ ...d, interpreter_required: e.target.checked }))} /> Interpreter required</label><label><input type="checkbox" checked={consentDraft.student_observation_consent} onChange={(e) => setConsentDraft((d) => ({ ...d, student_observation_consent: e.target.checked }))} /> Student observation consent</label><label><input type="checkbox" checked={consentDraft.photography_consent} onChange={(e) => setConsentDraft((d) => ({ ...d, photography_consent: e.target.checked }))} /> Roleplay photography consent</label><label><input type="checkbox" checked={consentDraft.information_sharing_consent} onChange={(e) => setConsentDraft((d) => ({ ...d, information_sharing_consent: e.target.checked }))} /> Roleplay information-sharing consent</label></div><label className="nhsuk-label">Notes</label><textarea className="nhsuk-textarea" rows="3" value={consentDraft.notes} onChange={(e) => setConsentDraft((d) => ({ ...d, notes: e.target.value }))} /><button className="nhsuk-button" disabled={working}>Save consent details</button></form>
      )}

      {tab === "incidents" && <div className="pguh-advanced-list"><div className="pguh-section-heading"><div><h3 className="nhsuk-heading-s">Linked DATIX incidents</h3><p>Incidents created from this patient record are linked here.</p></div><Link className="nhsuk-button nhsuk-button--secondary" to={`/intranet/datix?patient=${patientId}&name=${encodeURIComponent(patient?.full_name || "Patient")}`}>Create linked DATIX</Link></div>{incidents.length === 0 ? <p>No linked incidents.</p> : incidents.map((row) => <article key={row.id}><div><strong>{row.title}</strong><span>{row.severity || "Severity not set"}</span></div><p>{row.category || "Uncategorised"} · {row.location || "Location not set"}</p><small>{formatDateTime(row.incident_datetime)}</small></article>)}</div>}

      {tab === "wristband" && <div className="pguh-wristband-card"><div><span className="pguh-eyebrow">Roleplay patient identification</span><h3>{consentDraft.preferred_name || patient?.full_name || portalProfile?.roleplay_name || "Patient"}</h3><p><strong>Roleplay NHS:</strong> {formatRoleplayNhsNumber(patient?.roleplay_nhs_number || portalProfile?.roleplay_nhs_number)}</p><p><strong>Current location:</strong> {patient?.bed || patient?.location || "Not assigned"}</p></div><button type="button" className="nhsuk-button" onClick={printWristband}>Print wristband</button></div>}
    </section>
  );
}
